import styled from '@emotion/styled'
import {
    addMonths,
    eachDayOfInterval,
    endOfMonth,
    endOfWeek,
    format,
    isSameDay,
    isSameMonth,
    isToday,
    startOfMonth,
    startOfWeek,
    subMonths,
} from 'date-fns'
import { enUS, fr, ptBR } from 'date-fns/locale'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

interface CustomCalendarProps {
    value: Date | null
    onChange: (date: Date) => void
    onClickDay: (date: Date) => void
    tileContent: ({ date, view }: { date: Date; view: string }) => React.ReactNode
    tileClassName: ({ date, view }: { date: Date; view: string }) => string | null
    locale: string
    defaultValue?: Date
}

const locales = { fr, en: enUS, pt: ptBR }

const CustomCalendar = ({
    value,
    onChange,
    onClickDay,
    tileContent,
    tileClassName,
    locale,
    defaultValue = new Date(),
}: CustomCalendarProps) => {
    const { t } = useTranslation('translation')
    const [currentMonth, setCurrentMonth] = useState(defaultValue)
    const currentLocale = locales[locale as keyof typeof locales] || fr

    const handlePreviousMonth = () => {
        setCurrentMonth(subMonths(currentMonth, 1))
    }

    const handleNextMonth = () => {
        setCurrentMonth(addMonths(currentMonth, 1))
    }

    const handleToday = () => {
        const today = new Date()
        setCurrentMonth(today)
        onChange(today)
    }

    const monthStart = startOfMonth(currentMonth)
    const monthEnd = endOfMonth(currentMonth)
    const calendarStart = startOfWeek(monthStart, { weekStartsOn: 1 })
    const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 1 })
    const days = eachDayOfInterval({ start: calendarStart, end: calendarEnd })
    const numberOfRows = Math.ceil(days.length / 7)

    const handleDayClick = (date: Date) => {
        onChange(date)
        onClickDay(date)
    }

    const weekDays = Array.from({ length: 7 }, (_, i) => {
        const date = new Date(currentMonth)
        date.setDate(date.getDate() - date.getDay() + i + 1)
        return format(date, 'EEE', { locale: currentLocale })
    })

    return (
        <CalendarWrapper>
            <CalendarHeader>
                <MonthDisplay>{format(currentMonth, 'MMMM yyyy', { locale: currentLocale })}</MonthDisplay>
                <NavigationButtons>
                    <NavButton onClick={handlePreviousMonth} aria-label="Previous month">
                        ‹
                    </NavButton>
                    <TodayButton onClick={handleToday}>{t('global.today')}</TodayButton>
                    <NavButton onClick={handleNextMonth} aria-label="Next month">
                        ›
                    </NavButton>
                </NavigationButtons>
            </CalendarHeader>

            <WeekdaysRow>
                {weekDays.map((day) => (
                    <Weekday key={day}>{day}</Weekday>
                ))}
            </WeekdaysRow>

            <CalendarGrid numberOfRows={numberOfRows}>
                {days.map((date, index) => {
                    const isCurrentMonth = isSameMonth(date, currentMonth)
                    const isSelected = value !== null && isSameDay(date, value)
                    const isDayToday = isToday(date)
                    const customClassName = tileClassName({ date, view: 'month' })
                    const content = tileContent({ date, view: 'month' })

                    return (
                        <DayCell
                            key={index}
                            isCurrentMonth={isCurrentMonth}
                            isSelected={isSelected}
                            isToday={isDayToday}
                            customClassName={customClassName}
                            onClick={() => handleDayClick(date)}
                        >
                            <DayNumber isCurrentMonth={isCurrentMonth} isSelected={isSelected} isToday={isDayToday}>
                                {format(date, 'd')}
                            </DayNumber>
                            {content}
                        </DayCell>
                    )
                })}
            </CalendarGrid>
        </CalendarWrapper>
    )
}

const CalendarWrapper = styled.div`
    width: 100%;
    border: none;
    border-radius: 12px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    background: white;
    font-family: 'Roboto', sans-serif;
    overflow: hidden;

    @media (max-width: 750px) {
        border-radius: 8px;
    }
`

const CalendarHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 20px;
    border-bottom: 1px solid #e5e7eb;

    @media (max-width: 750px) {
        padding: 12px 16px;
    }
`

const MonthDisplay = styled.div`
    font-size: 1.8rem;
    font-weight: 600;
    color: #1f2937;
    font-family: 'Roboto Semi Bold';

    @media (max-width: 750px) {
        font-size: 1.4rem;
    }
`

const NavigationButtons = styled.div`
    display: flex;
    gap: 8px;
    align-items: center;

    @media (max-width: 750px) {
        gap: 4px;
    }
`

const NavButton = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    background: white;
    cursor: pointer;
    font-size: 1.2rem;
    color: #6b7280;
    transition: all 0.2s;
    font-size: 3rem;

    &:hover {
        background: #f9fafb;
        color: #1f2937;
    }

    &:active {
        background: #f3f4f6;
    }

    @media (max-width: 750px) {
        width: 32px;
        height: 32px;
        font-size: 1rem;
    }
`

const TodayButton = styled.button`
    padding: 0 16px;
    height: 36px;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    background: white;
    cursor: pointer;
    font-size: 1.9rem;
    font-weight: 500;
    color: #6b7280;
    transition: all 0.2s;

    &:hover {
        background: #f9fafb;
        color: #1f2937;
    }

    &:active {
        background: #f3f4f6;
    }

    @media (max-width: 750px) {
        padding: 0 12px;
        height: 32px;
        font-size: 1.8rem;
    }
`

const WeekdaysRow = styled.div`
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    padding: 12px 20px 8px;
    border-bottom: 1px solid #e5e7eb;

    @media (max-width: 750px) {
        padding: 8px 12px 6px;
    }
`

const Weekday = styled.div`
    text-align: center;
    font-size: 1.8rem;
    font-weight: 600;
    color: #9ca3af;
    text-transform: uppercase;

    @media (max-width: 750px) {
        font-size: 0.7rem;
    }
`

const CalendarGrid = styled.div<{ numberOfRows: number }>`
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    grid-template-rows: repeat(${(props) => props.numberOfRows}, 1fr);
    gap: 1px;
    background: #e5e7eb;
`

const DayCell = styled.div<{
    isCurrentMonth: boolean
    isSelected: boolean | null
    isToday: boolean
    customClassName?: string | null
}>`
    background: ${(props) => (props.isCurrentMonth ? 'white' : '#f9fafb')};
    min-height: 80px;
    padding: 8px;
    cursor: pointer;
    transition: background 0.2s;
    position: relative;

    &:hover {
        background: #f9fafb;
    }

    ${(props) =>
        props.isSelected &&
        `
        background: rgba(180, 0, 0, 0.1);
    `}

    ${(props) =>
        props.customClassName !== null &&
        props.customClassName !== undefined &&
        `
        ${props.customClassName}
    `}

    @media (max-width: 750px) {
        min-height: 60px;
        padding: 4px;
    }
`

const DayNumber = styled.span<{ isCurrentMonth: boolean; isSelected: boolean | null; isToday: boolean }>`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    font-size: 1.9rem;
    font-weight: 500;
    border-radius: 50%;
    margin-bottom: 4px;
    color: ${(props) => (props.isCurrentMonth ? '#1f2937' : '#9ca3af')};

    ${(props) =>
        props.isToday &&
        `
        background: var(--primary-color);
        color: white;
        font-weight: 600;
    `}

    ${(props) =>
        props.isSelected &&
        !props.isToday &&
        `
        background: rgba(180, 0, 0, 0.2);
        color: var(--primary-color);
        font-weight: 600;
    `}

    @media (max-width: 750px) {
        width: 24px;
        height: 24px;
        font-size: 1.8rem;
        margin-bottom: 2px;
    }
`

export default CustomCalendar
